// The two lighting rigs + the toggle that switches them. EDITING (default):
// flat ambient + hemisphere — every face reads near its true palette color, so
// nothing distracts from the shape being crafted. PRESENTATION (VR walkthrough,
// plus the Layout 3D "Lighting" preview): image-based lighting from a
// prefiltered RoomEnvironment, so MeshStandardMaterial surfaces pick up soft
// indirect light that varies with facing direction. The env map is prefiltered
// once at boot (one-time GPU cost); switching rigs just swaps scene.environment
// and light intensities.
//
// Owned as an installed slice: subsystems built AFTER this (the floor scene's
// sun + ground, the wall roof) hook in via the returned onLightingRig() rather
// than being referenced directly — applyLightingRig runs at install time, long
// before they exist.

import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { onState, uiState } from "../ui/state";

export interface LightingDeps {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  wakeRendering: () => void;
}

export interface LightingApi {
  // Register a reaction to a rig switch (called immediately with the current
  // state, then on every change). For furniture the rig owns indirectly — the
  // floor scene's sun/ground, the wall roof.
  onLightingRig: (fn: (on: boolean) => void) => void;
}

export function installLighting(deps: LightingDeps): LightingApi {
  const { scene, renderer, wakeRendering } = deps;

  const editAmbient = new THREE.AmbientLight(0xffffff, 1.0);
  const sharedHemi = new THREE.HemisphereLight(0xffffff, 0x808080, 2.0);
  scene.add(editAmbient, sharedHemi);
  const presentationEnvMap = (() => {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const tex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    return tex;
  })();
  // Presentation-rig ambient knob (inert while scene.environment is null).
  // Deliberately LOW: ambient light ignores geometry (no roof can block it),
  // so the only way a roofed interior reads darker than the sunlit exterior is
  // for the sun to dominate the light budget. Raising this washes interiors
  // out and makes rooms look lit from within.
  scene.environmentIntensity = 0.5;

  // Presentation sky: a gradient dome so windows/doors open onto a bright
  // daytime sky instead of the editor's dark void — the exterior must read
  // BRIGHTER than the interior or sunlight appears to come from inside the
  // room. Centered on the scene origin: the walkthrough keeps the player at
  // the origin (the world moves instead), so the player is always mid-dome.
  // Raw ShaderMaterial — ignores scene lights and ACES; colors are final.
  const skyDome = new THREE.Mesh(
    new THREE.SphereGeometry(200, 32, 16),
    new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        uZenith: { value: new THREE.Color(0x6f9fd8) },
        uHorizon: { value: new THREE.Color(0xdfeaf4) },
        uBelow: { value: new THREE.Color(0x252836) }, // meets the ground island's dark fade
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vDir;
        uniform vec3 uZenith;
        uniform vec3 uHorizon;
        uniform vec3 uBelow;
        void main() {
          float y = normalize(vDir).y;
          vec3 col = y >= 0.0
            ? mix(uHorizon, uZenith, smoothstep(0.0, 0.45, y))
            : mix(uHorizon, uBelow, smoothstep(0.0, -0.18, y));
          gl_FragColor = vec4(col, 1.0);
        }`,
    }),
  );
  // Same renderOrder as the immersive skybox but added later, so with both
  // visible (walkthrough) the sky draws second and covers it.
  skyDome.renderOrder = -1;
  skyDome.visible = false;
  scene.add(skyDome);

  function presentationLightingOn(): boolean {
    // One toggle (presentationLighting, off by default) governs the rig in BOTH
    // places it can show: the desktop floor-plan 3D view and the VR walkthrough.
    return (
      uiState.presentationLighting &&
      (uiState.walkthroughActive ||
        (uiState.mode === "floorplan" && uiState.floorView === "3d"))
    );
  }
  const _rigHooks: Array<(on: boolean) => void> = [];
  function onLightingRig(fn: (on: boolean) => void): void {
    _rigHooks.push(fn);
    fn(presentationLightingOn());
  }
  function applyLightingRig(): void {
    const on = presentationLightingOn();
    scene.environment = on ? presentationEnvMap : null;
    editAmbient.visible = !on;
    skyDome.visible = on;
    // Under the sun-dominant presentation budget the hemisphere is only a low
    // fill that keeps bottom faces from going muddy; in the editing rig it
    // carries the whole up/down gradient.
    sharedHemi.intensity = on ? 0.3 : 2.0;
    // Filmic response only in the presentation rig: the editing rig must show
    // exact palette colors. Three recompiles affected materials on its own when
    // renderer.toneMapping changes (it's part of each material's program key).
    renderer.toneMapping = on ? THREE.ACESFilmicToneMapping : THREE.NoToneMapping;
    // THE overall-brightness knob (a uniform — no recompile): scales the whole
    // presentation exposure while preserving the sun:ambient ratio that keeps
    // interiors reading darker than the sunlit outside. Prefer adjusting this
    // over individual light intensities.
    renderer.toneMappingExposure = on ? 0.85 : 1.0;
    // Walkthrough: the scene is static and the sun rides inside floorScene.root
    // (teleports move light + room rigidly together), so the shadow map is baked
    // ONCE at entry and is free for the rest of the session — the Quest never
    // pays a per-frame shadow pass. Desktop preview keeps live shadows; the
    // layout is still editable there.
    if (uiState.walkthroughActive) {
      renderer.shadowMap.autoUpdate = false;
      renderer.shadowMap.needsUpdate = true;
    } else {
      renderer.shadowMap.autoUpdate = true;
    }
    for (const fn of _rigHooks) fn(on);
    wakeRendering(); // walkthrough exit flips the rig from a session callback, not an input
  }
  for (const k of [
    "walkthroughActive",
    "presentationLighting",
    "floorView",
    "mode",
  ] as const) {
    onState(k, applyLightingRig);
  }
  applyLightingRig();

  return { onLightingRig };
}
